import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Radio } from '@creditornot/cb-radio';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { Tag } from '@creditornot/cb-tag';
import { pxToRem } from '@creditornot/cb-utils';
import { ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { ValidationErrors } from 'final-form';
import { DateTime } from 'luxon';
import React, { ReactNode } from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import { useAdCampaignTranslationKeys } from 'modules/campaigns/ad-campaign/hooks/useAdCampaignTranslationKeys';
import { AdCampaignScheduleFormValues, AdCampaignScheduleStrategy, CampaignStatus } from 'modules/campaigns/types';

import { AdCampaignDatePicker } from './AdCampaignDatePicker';

const AdCampaignScheduleFormModalContainer = styled.div`
  background-color: ${cbSemanticCssVars.surfaceMain};
  min-width: 800px;
  border-radius: 8px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: auto;
    width: 98vw;
  }
`;

const StyledSection = styled.div`
  color: ${cbSemanticCssVars.textPrimary};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 800px;

  &:not(:last-of-type) {
    border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const StyledSectionContent = styled.div`
  display: flex;
  align-content: center;
  align-items: flex-start;
  gap: 8px;

  > div {
    flex: 1;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ScheduleStrategyRadioContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: column;
  gap: 16px;
`;

const StyledFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const StyledErrorMessage = styled(Alert).attrs({ variant: 'error' })`
  background-color: ${cssThemedValue({ light: colors.strawberry8, dark: colors.strawberry16 })};
  color: ${cbSemanticCssVars.textPrimary};
  padding: ${pxToRem(8)};
`;

const StyledTag = styled(Tag)`
  letter-spacing: 0.5px;
  line-height: 1em;
  margin-inline-start: 8px;
`;

const StyledDateContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

type AdsFreeTrialConfig = {
  durationWeeks: number;
  freeTrialDaysMinimum: number;
  freeTrialPlusPaidCampaignMinimumDays: number;
};

const Bold = styled.span`
  font-weight: bold;
`;

function getDisabledDaysForEndDate(
  startDate: string,
  isFreeTrialSelected: boolean,
  adsFreeTrialConfig?: AdsFreeTrialConfig,
) {
  if (!isFreeTrialSelected) {
    // if Free trials is not selected, the end date should be after the start date
    // as backend can't create campaigns with 1 day duration
    return { before: DateTime.fromISO(startDate).plus({ days: 1 }).toJSDate() };
  }

  return [
    // As backend can't create campaigns with 1 day duration, the end date can't be the same as the free trials
    // duration plus start date, f. ex. (14 days free trial + 1 day start date) = 15 days.
    DateTime.fromISO(startDate)
      .plus({ weeks: adsFreeTrialConfig?.durationWeeks ?? 0 })
      .toJSDate(),
    {
      // The end date should not allow the user to select a date before than free trials
      // duration, at least same as the freeTrialDaysMinimum (14) days.
      before: DateTime.fromISO(startDate)
        .plus({ weeks: adsFreeTrialConfig?.durationWeeks ?? 0 })
        .minus({ days: 1 })
        .toJSDate(),
    },
  ];
}

type AdCampaignScheduleFormModalProps = {
  adsFreeTrialConfig?: AdsFreeTrialConfig;
  errors?: ValidationErrors;
  scheduleStrategy: AdCampaignScheduleStrategy;
  campaignStatus?: CampaignStatus;
  isDisabled?: boolean;
  isDirty: boolean;
  isFreeTrialSelected: boolean;
  startDate: string;
  onCloseClick(): void;
  onSubmit(): void;
};

export const AdCampaignScheduleFormModal = ({
  adsFreeTrialConfig,
  onSubmit,
  scheduleStrategy,
  errors,
  campaignStatus,
  onCloseClick,
  isDirty,
  isFreeTrialSelected,
  startDate,
}: AdCampaignScheduleFormModalProps) => {
  const countrySpecificTranslationKeys = useAdCampaignTranslationKeys();
  const { freeTrialDaysMinimum = 0, freeTrialPlusPaidCampaignMinimumDays = 0 } = adsFreeTrialConfig ?? {};
  const { formatMessage } = useIntl();
  const disabledDays = {
    before: new Date(),
  };
  const endDateDisabledDays = getDisabledDaysForEndDate(startDate, isFreeTrialSelected, adsFreeTrialConfig);
  const dateChangeDisabled = campaignStatus ? ['completed', 'running', 'paused'].includes(campaignStatus) : false;

  return (
    <AdCampaignScheduleFormModalContainer>
      <ModalHeader onCloseClick={onCloseClick}>
        <FormattedMessage id="campaigns.form.scheduling.label" />
      </ModalHeader>
      <StyledSection>
        <Typography.Body3>
          <FormattedMessage
            id={countrySpecificTranslationKeys.scheduleStrategyDescription}
            values={{ bold: (innerText: ReactNode) => <Bold>{innerText}</Bold> }}
          />
        </Typography.Body3>
        <ScheduleStrategyRadioContainer>
          <Field<AdCampaignScheduleFormValues['scheduleStrategy']>
            name="scheduleStrategy"
            type="radio"
            value="alwaysOn"
          >
            {({ input }) => {
              return (
                <Radio {...input}>
                  <Typography.Body3>
                    <FormattedMessage id="campaigns.ads.schedule.strategy-always-on-option" />
                  </Typography.Body3>
                  <StyledTag variant="lightBlack">
                    <Typography.Overline4>
                      <FormattedMessage id="campaigns.recommended" />
                    </Typography.Overline4>
                  </StyledTag>
                </Radio>
              );
            }}
          </Field>
          <Field<AdCampaignScheduleFormValues['scheduleStrategy']>
            name="scheduleStrategy"
            type="radio"
            value="selectDates"
          >
            {({ input }) => (
              <Radio {...input}>
                <Typography.Body3>
                  <FormattedMessage id="campaigns.ads.schedule.strategy-select-dates-option" />
                </Typography.Body3>
              </Radio>
            )}
          </Field>
        </ScheduleStrategyRadioContainer>
      </StyledSection>
      {scheduleStrategy === 'selectDates' && (
        <StyledSection>
          <Typography.Body2 fontWeight="bold">
            <FormattedMessage id="campaigns.form.scheduling.dates.label" />
          </Typography.Body2>

          <StyledSectionContent>
            <Field<AdCampaignScheduleFormValues['startDate']> name="startDate" type="date">
              {({ input }) => {
                return (
                  <StyledDateContainer>
                    <AdCampaignDatePicker
                      {...input}
                      value={input.value ? DateTime.fromISO(input.value) : undefined}
                      onChange={value => input.onChange(value?.toISO())}
                      disabled={dateChangeDisabled}
                      placeholder={formatMessage({ id: 'campaigns.labels.start-date' })}
                      defaultValueOption="empty"
                      disabledDays={disabledDays}
                    />
                  </StyledDateContainer>
                );
              }}
            </Field>

            <Field<AdCampaignScheduleFormValues['endDate']> name="endDate" type="date">
              {({ input }) => {
                return (
                  <StyledDateContainer>
                    <AdCampaignDatePicker
                      {...input}
                      value={
                        input.value
                          ? DateTime.fromISO(input.value).set({ hour: 11, minute: 59, second: 59, millisecond: 0 })
                          : undefined
                      }
                      onChange={value => input.onChange(value?.toISO())}
                      disabled={dateChangeDisabled}
                      placeholder={formatMessage({ id: 'campaigns.labels.end-date' })}
                      defaultValueOption="empty"
                      disabledDays={endDateDisabledDays}
                    />
                  </StyledDateContainer>
                );
              }}
            </Field>
          </StyledSectionContent>

          {!!errors?.endDate && (
            <StyledErrorMessage>
              <FormattedMessage
                id={errors.endDate}
                values={{ freeTrialDaysMinimum, freeTrialPlusPaidCampaignMinimumDays }}
              />
            </StyledErrorMessage>
          )}

          {!!errors?.startDate && (
            <StyledErrorMessage>
              <FormattedMessage id={errors.startDate} />
            </StyledErrorMessage>
          )}
        </StyledSection>
      )}
      {scheduleStrategy === 'alwaysOn' && (
        <StyledSection>
          <Typography.Body2 fontWeight="bold">
            <FormattedMessage id="campaigns.labels.start-date" />
          </Typography.Body2>

          <StyledSectionContent>
            <Field<AdCampaignScheduleFormValues['startDate']> name="startDate" type="date">
              {({ input }) => {
                return (
                  <AdCampaignDatePicker
                    {...input}
                    value={input.value ? DateTime.fromISO(input.value) : undefined}
                    onChange={value => input.onChange(value?.toISO())}
                    isInvalid={!!errors?.startDate}
                    disabled={dateChangeDisabled}
                    placeholder={formatMessage({ id: 'campaigns.labels.start-date' })}
                    defaultValueOption="today"
                    disabledDays={disabledDays}
                  />
                );
              }}
            </Field>
          </StyledSectionContent>
        </StyledSection>
      )}
      <StyledFooter>
        <Button
          disabled={!(isDirty && (!errors || !Object.keys(errors).length))}
          variant="primary"
          size="small"
          onClick={onSubmit}
        >
          <FormattedMessage id="campaigns.form.update" />
        </Button>
      </StyledFooter>
    </AdCampaignScheduleFormModalContainer>
  );
};
